import { useState } from "react";
import type { RegistrationResponseJSON } from "@simplewebauthn/browser";
import { startRegistration } from "@simplewebauthn/browser";

import { fetchCurrentUser } from "@js/apps/auth/actions";
import type { PasskeyConfirmation } from "@js/apps/auth/api";
import {
  useBeginPasskeyRegistrationMutation,
  useCompletePasskeyRegistrationMutation,
} from "@js/apps/auth/api";
import { Snackbar } from "@js/components/snackbar";
import { useAppDispatch } from "@js/hooks";

export const useRegisterPasskey = () => {
  const dispatch = useAppDispatch();
  const [beginPasskeyRegistration] = useBeginPasskeyRegistrationMutation();
  const [completePasskeyRegistration] =
    useCompletePasskeyRegistrationMutation();
  const [isRegistering, setIsRegistering] = useState(false);

  const register = async (confirmation: PasskeyConfirmation) => {
    setIsRegistering(true);

    let credential: RegistrationResponseJSON | undefined;
    try {
      const options = await beginPasskeyRegistration().unwrap();
      credential = await startRegistration({ optionsJSON: options });
    } catch (error) {
      const errorMessage =
        !!error && error instanceof Error && error.name !== "NotAllowedError"
          ? error?.message
          : undefined;
      const message =
        errorMessage || "Failed to add passkey. Please try again.";
      Snackbar.error(message);
      setIsRegistering(false);
      throw error;
    }

    try {
      await completePasskeyRegistration({ credential, confirmation }).unwrap();
      void dispatch(fetchCurrentUser());
    } finally {
      setIsRegistering(false);
    }
  };

  return { register, isRegistering };
};
